const AXES = ["x", "y", "z"] as const;

export type AccelerometerAxis = (typeof AXES)[number];

export interface SignedAccelerometerVector {
  x: number;
  y: number;
  z: number;
}

export interface AxisAngleRotation {
  x: number;
  y: number;
  z: number;
  angleRad: number;
}

export interface AccelerometerOrientationSample
  extends SignedAccelerometerVector {
  sampleIndex: number;
  magnitude: number;
  primaryAxis: AccelerometerAxis;
  secondaryAxis: AccelerometerAxis;
  primaryFaceLabel: string;
  secondaryFaceLabel: string;
  isBetweenFaces: boolean;
  orientationLabel: string;
  orientationDetail: string;
  xTiltDegrees: number;
  yTiltDegrees: number;
  zTiltDegrees: number;
  rotation: AxisAngleRotation;
}

const BETWEEN_FACE_THRESHOLD = 0.1;
const EPSILON = 1e-6;
const UPRIGHT_REFERENCE_VECTOR: SignedAccelerometerVector = {
  x: -0.5,
  y: 0,
  z: 0,
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const vectorMagnitude = ({ x, y, z }: SignedAccelerometerVector) =>
  Math.hypot(x, y, z);

const normalizeVector = (
  vector: SignedAccelerometerVector
): SignedAccelerometerVector => {
  const magnitude = vectorMagnitude(vector);
  if (magnitude < EPSILON) {
    return { ...UPRIGHT_REFERENCE_VECTOR };
  }

  return {
    x: vector.x / magnitude,
    y: vector.y / magnitude,
    z: vector.z / magnitude,
  };
};

const quaternionToAxisAngle = ({
  x,
  y,
  z,
  w,
}: {
  x: number;
  y: number;
  z: number;
  w: number;
}): AxisAngleRotation => {
  const clampedW = clamp(w, -1, 1);
  const angleRad = 2 * Math.acos(clampedW);
  const scale = Math.sqrt(Math.max(1 - clampedW * clampedW, 0));

  if (scale < EPSILON || angleRad < EPSILON) {
    return {
      x: 1,
      y: 0,
      z: 0,
      angleRad: 0,
    };
  }

  return {
    x: x / scale,
    y: y / scale,
    z: z / scale,
    angleRad,
  };
};

const quaternionFromUnitVectors = (
  from: SignedAccelerometerVector,
  to: SignedAccelerometerVector
) => {
  const dot = from.x * to.x + from.y * to.y + from.z * to.z;

  if (dot < -1 + EPSILON) {
    const orthogonal =
      Math.abs(from.x) < 0.9
        ? { x: 1, y: 0, z: 0 }
        : { x: 0, y: 1, z: 0 };
    const axis = normalizeVector({
      x: from.y * orthogonal.z - from.z * orthogonal.y,
      y: from.z * orthogonal.x - from.x * orthogonal.z,
      z: from.x * orthogonal.y - from.y * orthogonal.x,
    });

    return {
      x: axis.x,
      y: axis.y,
      z: axis.z,
      w: 0,
    };
  }

  const cross = {
    x: from.y * to.z - from.z * to.y,
    y: from.z * to.x - from.x * to.z,
    z: from.x * to.y - from.y * to.x,
  };
  const quaternion = {
    x: cross.x,
    y: cross.y,
    z: cross.z,
    w: dot + 1,
  };
  const magnitude = Math.hypot(
    quaternion.x,
    quaternion.y,
    quaternion.z,
    quaternion.w
  );

  if (magnitude < EPSILON) {
    return { x: 0, y: 0, z: 0, w: 1 };
  }

  return {
    x: quaternion.x / magnitude,
    y: quaternion.y / magnitude,
    z: quaternion.z / magnitude,
    w: quaternion.w / magnitude,
  };
};

const getFaceLabel = (
  axis: AccelerometerAxis,
  value: number,
  { lowercase = false }: { lowercase?: boolean } = {}
) => {
  const sign = value >= 0 ? "positive" : "negative";

  const label =
    axis === "x"
      ? sign === "negative"
        ? "Upright"
        : "Upside down"
      : axis === "y"
        ? sign === "negative"
          ? "Left side down"
          : "Right side down"
        : sign === "negative"
          ? "Front side down"
          : "Back side down";

  return lowercase ? label.toLowerCase() : label;
};

const describeOrientation = (
  primaryAxis: AccelerometerAxis,
  primaryValue: number,
  secondaryAxis: AccelerometerAxis,
  secondaryValue: number,
  isBetweenFaces: boolean
) => {
  const primaryFaceLabel = getFaceLabel(primaryAxis, primaryValue);
  const secondaryFaceLabel = getFaceLabel(secondaryAxis, secondaryValue);

  if (isBetweenFaces) {
    return {
      primaryFaceLabel,
      secondaryFaceLabel,
      orientationLabel: `Between ${primaryFaceLabel.toLowerCase()} and ${secondaryFaceLabel.toLowerCase()}`,
      orientationDetail: "Balanced between the two dominant faces.",
    };
  }

  const secondaryMagnitude = Math.abs(secondaryValue);

  return {
    primaryFaceLabel,
    secondaryFaceLabel,
    orientationLabel: primaryFaceLabel,
    orientationDetail:
      secondaryMagnitude < 0.05
        ? "Minimal secondary tilt."
        : `Tilting toward ${secondaryFaceLabel.toLowerCase()}.`,
  };
};

export const normaliseWrappedAxis = (rawValue: number): number => {
  if (!Number.isFinite(rawValue)) {
    return 0;
  }

  if (rawValue > 4) {
    return clamp(rawValue - 8, -0.5, 0.5);
  }

  return clamp(rawValue, -0.5, 0.5);
};

export const toDisplayAxisValue = (signedValue: number): number =>
  clamp(signedValue + 0.5, 0, 1);

export const calculateAxisTiltDegrees = (
  axisValue: number,
  otherAxisOne: number,
  otherAxisTwo: number
) => {
  const planeMagnitude = Math.hypot(otherAxisOne, otherAxisTwo);
  return (Math.atan2(axisValue, planeMagnitude) * 180) / Math.PI;
};

export const createOrientationSample = (
  sampleIndex: number,
  vector: SignedAccelerometerVector
): AccelerometerOrientationSample => {
  const axisRanking = AXES.map((axis) => ({
    axis,
    value: vector[axis],
    magnitude: Math.abs(vector[axis]),
  })).sort((left, right) => right.magnitude - left.magnitude);
  const [primaryAxis, secondaryAxis] = axisRanking;
  const isBetweenFaces =
    primaryAxis.magnitude - secondaryAxis.magnitude <= BETWEEN_FACE_THRESHOLD;
  const description = describeOrientation(
    primaryAxis.axis,
    primaryAxis.value,
    secondaryAxis.axis,
    secondaryAxis.value,
    isBetweenFaces
  );
  const rotationQuaternion = quaternionFromUnitVectors(
    normalizeVector(UPRIGHT_REFERENCE_VECTOR),
    normalizeVector(vector)
  );

  return {
    sampleIndex,
    ...vector,
    magnitude: vectorMagnitude(vector),
    primaryAxis: primaryAxis.axis,
    secondaryAxis: secondaryAxis.axis,
    primaryFaceLabel: description.primaryFaceLabel,
    secondaryFaceLabel: description.secondaryFaceLabel,
    isBetweenFaces,
    orientationLabel: description.orientationLabel,
    orientationDetail: description.orientationDetail,
    xTiltDegrees: calculateAxisTiltDegrees(vector.x, vector.y, vector.z),
    yTiltDegrees: calculateAxisTiltDegrees(vector.y, vector.x, vector.z),
    zTiltDegrees: calculateAxisTiltDegrees(vector.z, vector.x, vector.y),
    rotation: quaternionToAxisAngle(rotationQuaternion),
  };
};

export const createDefaultOrientationSample = () =>
  createOrientationSample(0, UPRIGHT_REFERENCE_VECTOR);
